/**
 * Référentiels Qualité administrables (QUAL-001 §5.3) — CRUD + garde d'usage.
 *
 * Patron cloné de `useLeadTypes.ts` (CLI-001) : même `headers()` (x-entity-id),
 * `staleTime: 300_000` sur la liste, `staleTime: 0` sur l'usage, tolérance 204
 * au DELETE, invalidation `refetchType: 'all'`. Le `code`/slug est dérivé
 * serveur depuis le label — jamais fourni côté client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'

import type {
    QualityReferentialItem,
    QualityReferentialListResponse,
    QualityReferentialType,
    QualityReferentialUpdate,
    QualityReferentialUsage,
} from './useQuality.types'

const REFERENTIALS_ENDPOINT = `${API_BASE}/quality/referentials`

function headers(entityId: string | null | undefined): HeadersInit {
    const h: Record<string, string> = { 'content-type': 'application/json' }
    if (entityId) h['x-entity-id'] = entityId
    return h
}

function invalidateReferentials(qc: ReturnType<typeof useQueryClient>) {
    void qc.invalidateQueries({
        queryKey: queryKeys.qualityReferentials.all,
        refetchType: 'all',
    })
}

/**
 * Valeurs ACTIVES d'un type de référentiel sur le périmètre HÉRITÉ (entité
 * courante + ancêtres), triées par position.
 *
 * Il n'existe pas de variante « toutes les valeurs » : les référentiels se
 * gèrent EXCLUSIVEMENT depuis le sélecteur de la fiche (créer / renommer /
 * supprimer), et retirer une valeur n'est pas un cul-de-sac — la création est
 * RÉACTIVANTE côté serveur (`ON CONFLICT … SET is_active = TRUE`), donc retaper
 * le même libellé la ressuscite avec son code d'origine, et les fiches qui le
 * portaient retrouvent leur libellé. Décision PO du 2026-08-09 : pas d'écran de
 * réglages dédié.
 */
export function useQualityReferentials(
    entityId: string | null | undefined,
    refType: QualityReferentialType,
    // ⚠️ DEUX périmètres, pas un. `entry` (défaut) sert à SAISIR une valeur sur une
    // fiche : self ∪ ancêtres, sinon une agence classerait ses fiches avec le
    // vocabulaire d'une agence sœur. `filter` sert à FILTRER ou REGROUPER un registre
    // consolidé : il ajoute les entités rattachées, sans quoi une valeur créée par une
    // agence s'affichait dans la colonne mais restait introuvable au filtre, et le
    // regroupement la titrait avec son code technique.
    usage: 'entry' | 'filter' = 'entry',
) {
    return useQuery<QualityReferentialListResponse>({
        queryKey: queryKeys.qualityReferentials.list(entityId ?? '', refType, usage),
        queryFn: async () => {
            const qs = `?ref_type=${refType}&usage=${usage}`
            const response = await apiFetch(`${REFERENTIALS_ENDPOINT}${qs}`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId,
        staleTime: 300_000,
    })
}

export function useCreateQualityReferential(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<
        QualityReferentialItem,
        Error,
        { ref_type: QualityReferentialType; label: string }
    >({
        mutationFn: async (body) => {
            const response = await apiFetch(REFERENTIALS_ENDPOINT, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(body),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => invalidateReferentials(qc),
    })
}

export function useUpdateQualityReferential(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<
        QualityReferentialItem,
        Error,
        { id: string; patch: QualityReferentialUpdate }
    >({
        mutationFn: async ({ id, patch }) => {
            const response = await apiFetch(`${REFERENTIALS_ENDPOINT}/${id}`, {
                method: 'PATCH',
                headers: headers(entityId),
                body: JSON.stringify(patch),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => invalidateReferentials(qc),
    })
}

/** Suppression logique (réaffectation facultative des NC référençant le code). */
export function useDeleteQualityReferential(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<void, Error, { id: string; reassignTo?: string | null }>({
        mutationFn: async ({ id, reassignTo }) => {
            const url = reassignTo
                ? `${REFERENTIALS_ENDPOINT}/${id}?reassign_to=${reassignTo}`
                : `${REFERENTIALS_ENDPOINT}/${id}`
            const response = await apiFetch(url, {
                method: 'DELETE',
                headers: headers(entityId),
            })
            if (!response.ok && response.status !== 204) await handleApiError(response)
        },
        onSuccess: () => invalidateReferentials(qc),
    })
}

/** Compteur d'usage (NC référençant le code) — alimente le dialogue de suppression. */
export function useQualityReferentialUsage(
    refId: string | null | undefined,
    entityId: string | null | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery<QualityReferentialUsage>({
        queryKey: queryKeys.qualityReferentials.usage(refId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${REFERENTIALS_ENDPOINT}/${refId}/usage`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: options?.enabled !== false && !!refId && !!entityId,
        staleTime: 0,
    })
}
